import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileCheck, Search, Users, Award, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../mock';
import ContactPopup from '../components/ContactPopup';

const Services = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Services</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive intellectual property solutions tailored to your business needs. From trademark registration to business formation, we provide expert guidance every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = {
                shield: Shield,
                'file-text': FileCheck,
                building: Users,
                search: Search,
                'shopping-bag': Award
              }[service.icon];

              return (
                <Card key={service.id} className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/10 group">
                  <CardHeader>
                    <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="h-7 w-7 text-amber-500" />
                    </div>
                    <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">{service.shortDesc}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-300 text-sm">
                          <CheckCircle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-end">
                      <Link to={service.slug}>
                        <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10">
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Choose Our Services
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Experience the difference of working with IP protection experts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
              <CardHeader>
                <CardTitle className="text-white text-xl">Comprehensive Protection</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-3">
                <p>
                  We don't just file paperwork – we provide strategic guidance to ensure your intellectual property is fully protected. Our holistic approach considers your business goals and industry landscape.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
              <CardHeader>
                <CardTitle className="text-white text-xl">Affordable Pricing</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-3">
                <p>
                  Quality IP protection shouldn't break the bank. We offer competitive, transparent pricing with no hidden fees. You'll know exactly what you're paying for upfront.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
              <CardHeader>
                <CardTitle className="text-white text-xl">Fast & Efficient</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-3">
                <p>
                  Time is money in business. Our streamlined processes and experienced team ensure your applications are filed quickly and correctly the first time, avoiding costly delays.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
              <CardHeader>
                <CardTitle className="text-white text-xl">Ongoing Support</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-400 space-y-3">
                <p>
                  Your relationship with us doesn't end at registration. We provide ongoing monitoring, renewal reminders, and support to ensure your IP remains protected for years to come.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-2xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Schedule a free consultation to discuss your intellectual property needs.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default Services;
